import {useMemo, useState} from 'react';
import {compose} from '../../lib/compose';
import {Button, Row, Col, Form} from 'react-bootstrap';
import withAlerts from '../../lib/withAlerts';
import {usePolicyMutations} from './policyMutations';
import {usePortalContext} from '../../lib/context/portalContext';
import {useCustomerContext} from '../../lib/context/customerContext';
import {useForm, FormProvider} from "react-hook-form";
import DeletePolicyConfirmModal from './deletePolicyConfirmModal';
import {ErrorMessage} from '@hookform/error-message';
import { gql } from "@apollo/client";
import { useLazyQuery } from "@apollo/client/react";
import PolicyForm from './policyForm';
import {Can} from '../auth/can';

const EditPolicy = ({minPolicy, alerts = {}, clicked = ''}) => {
  const {togglePortal, rowData} = usePortalContext();
  const [thresholdError, setThresholdError] = useState(null);
  const [existsError, setExistsError] = useState(null);
  const [defaultExistsError, setDefaultExistsError] = useState(null);
  const [minimumError, setMinimumError] = useState(false);
  const {success,error} = alerts;
  const {edit, remove} = usePolicyMutations();
  const [getPolicy] = useLazyQuery(GET_POLICY);
  const {hasPermission} = useCustomerContext();

  // RBAC: POLICY_EDIT - "On off-canvas: (a) Disable all policy information
  // UI elements; (b) Disable the 'Save Policy' button." Policy CREATION is
  // already gated upstream by POLICY_CREATE disabling the 'Create Policy'
  // button in index.jsx, so this component (row-click edit only, never
  // used for creation) only needs to check POLICY_EDIT.
  const policyEditDisabled = !hasPermission('POLICY_EDIT');
  
  const methods = useForm({
    mode: 'all',
    defaultValues: useMemo(() => {
      //set up the default booleans
      const defaults = {policy_action: {
        antisurveillance_mdm_action: 0,
        cellular_mdm_action: 0,
        low_battery_mdm_action: 0,
        antisurveillance_edge_action: 0,
        cellular_edge_action: 0,
        low_battery_edge_action: 0,
      }};

      //shallow copy the rowData to mutate
      const rD = {...rowData};
      
      //merging defaultValues into rowData overwriting nulls
      for (const key in rD) {
        if (rD.hasOwnProperty(key)) {
          if (rD[key] !== null && typeof rD[key] !== 'undefined'){
            defaults[key] = rD[key];
          }
        }
      }

      return defaults;
    }, [rowData])
  });

  const {setFocus, formState: {errors}} = methods || {}; 
  const handleDeletePolicy = async () => {
    const deletePolicy = await remove({variables:{policy_id: rowData?.id}});

    if(deletePolicy?.errors) {
      error({msg: 'Policy deletion failed.'});
    } else {
      success({msg: `Policy deleted.`});
      togglePortal();
    }
  }

  const removeDuplicateValues = (newObject, originalObject) => {
    for (const property in newObject) {
      if (originalObject[property] === newObject[property]) {
          delete newObject[property];
      }
    }
  }

  const onSubmit = async (formData) => {
    delete formData?.policy_action?.__typename;

    //reshape the policy object since I loaded the entire policy into formData defaultValues
    const policyShape = {
      is_phone_protected: true, //formData.is_phone_protected,
      has_battery_warning: formData.has_battery_warning,
      is_cellular_connected: formData.is_cellular_connected,
      is_default_visitor: formData.is_default_visitor,
      battery_warning_threshold: formData.battery_warning_threshold,
      name: formData.name,
      is_phone_mismatch: formData.is_phone_mismatch,
      protected_profile: formData.protected_profile,
      policy_action: formData?.policy_action
    };

    //Verify battery percentage is there if rule is true
    if(policyShape.has_battery_warning) {  
      //We set a battery rule
      policyShape.battery_warning_threshold = formData.battery_warning_threshold ?? 0;
    }
    
    //throw an error if the threshold isnt set
    if( policyShape.has_battery_warning && (formData.battery_warning_threshold === 0 || formData.battery_warning_threshold === undefined)) {
      setThresholdError('Battery Level Sufficient percentage is required.');
      return;
    }
    
    //attempt to strip duplicate values from policy update to prevent excess logging
    removeDuplicateValues(policyShape, rowData);
    
    const variables = {
      variables: {
        filters: {
          field: "name",
          value: `${formData?.name}`
        }
      }
    };

    //lazyQuery to inline validate against existing policies
    let policyNameInUse;
    const checkExistingPolicy = await getPolicy(variables);
    if(checkExistingPolicy?.data?.Policies?.result?.length > 0) {
      const checkPolicies = checkExistingPolicy?.data?.Policies?.result || [];

      //prevent inline error when editing existing policy. name check is positive when editing policy so compare id
      policyNameInUse = checkPolicies.find(p => (p.id !== rowData?.id && p.name === formData.name) );
      
      if(policyNameInUse) {
        //Trying to rename to an existing other policy name
        setExistsError('Policy Name already exists.');
        setFocus('name');
        return;
      }
    }

    const extendedPolicyShape = {policy_id: rowData?.id, ...policyShape};

    const editPolicy = await edit({variables: {policy: extendedPolicyShape}});
    
    if(editPolicy?.errors) {
      error({msg: 'There was an error editing the policy. Please try again.'});
    } else {
      success({msg: `Policy updated.`});
      togglePortal();
    }
  }

  return (
    <FormProvider {...methods}>
      <Form onSubmit={methods.handleSubmit(onSubmit)}>
        <fieldset disabled={policyEditDisabled}>
          <PolicyForm
            minPolicy={minPolicy}
            existsError={existsError}
            setExistsError={setExistsError}
            thresholdError={thresholdError}
            setThresholdError={setThresholdError}
            defaultExistsError={defaultExistsError}
            setDefaultExistsError={setDefaultExistsError}
            setMinimumError={setMinimumError}
            clicked={clicked} //tells policy form the origin
          />
        </fieldset>
        <Row>
          <Col>
            <div className="d-grid gap-2">
              <Button
                className="mb-3"
                type="submit"
                disabled={policyEditDisabled || minimumError || existsError || defaultExistsError || thresholdError || methods?.errors?.name}
                >Save Policy</Button>

              <div className="mb-3">
                <ErrorMessage
                  errors={methods?.errors}
                  name="name"
                  render={({ message }) => <strong>{message}</strong>}
                />

                {minimumError && <strong className="mb-3 d-block">Invalid Policy. Minimum requirements unmet.<br/></strong>}
                {errors?.policy_action && <strong className="mb-3 d-block">MDM Actions must be selected when MDM feature is enabled.<br/></strong>}
                {existsError && <strong className="mb-3 d-block">{existsError}<br/></strong>}
                {defaultExistsError && <strong className="mb-3 d-block">{defaultExistsError}<br/></strong>}
                {thresholdError && <strong className="mb-3 d-block">{thresholdError}<br/></strong>}
              </div>
            </div>
          </Col>
        </Row>
        {rowData?.name ? 
        <Row className="mb-3">
          <Col>
            <Can code="POLICY_DELETE">
              <DeletePolicyConfirmModal onChange={handleDeletePolicy} scope={"policy"} 
              message={`Deleting this policy will leave ${rowData?.assignmentCount} User-SafeCase pair(s) without a policy.`} assignmentcount={rowData?.assignmentCount} />
            </Can>
          </Col>
        </Row>
        : null }
      </Form>
    </FormProvider>
    
  )
}

const GET_POLICY = gql`
  query Policies ($filters: GenericFilters) {
    Policies (filters: $filters) {
      result {
        id
        name
        is_default_visitor
      }
    }
  }
`;


export default compose(
  withAlerts,
)(EditPolicy);
